import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { dbHelpers } from '../../db/database';
import { stocksAPI } from '../../api/stocks';
import syncService from '../../services/syncService';


import './StockAdjustment.css';



function StockAdjustment() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [adjustment, setAdjustment] = useState(0);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const location = useLocation();
  const { variantId, warehouseId, variantDetails } = location.state || {};

  useEffect(() => {
    loadData();
  }, [productId]);


  useEffect(() => {
  if (warehouseId) {
    setSelectedWarehouse(warehouseId);
  }
}, [warehouseId]);

  async function loadData() {
    try {
      const prod = await dbHelpers.getProductById(productId);
      const stks = await dbHelpers.getStocksByProduct(productId);

      if (variantId) {
      stks = stks.filter(s => String(s.variant) === String(variantId));
    }

      setProduct(prod);
      setStocks(stks);
    } catch (err) {
      setError('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  }

  async function handleAdjust() {
    setError('');

    // Validation
    if (!selectedWarehouse) {
      setError('Please select a warehouse');
      return;
    }

    if (adjustment === 0 || !adjustment) {
      setError('Please enter an adjustment amount');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for adjustment');
      return;
    }

    const stock = stocks.find(s =>
      String(s.warehouse) === String(selectedWarehouse) ||
      String(s.warehouse_id) === String(selectedWarehouse)
    );

    if (!stock) {
      setError('Stock record not found');
      return;
    }

    const adjustmentValue = parseInt(adjustment);
    const newQuantity = (stock.quantity || 0) + adjustmentValue;

    if (newQuantity < 0) {
      setError(`Cannot reduce stock below zero. Current: ${stock.quantity}, Adjustment: ${adjustmentValue}`);
      return;
    }

    setSubmitting(true);

    try {
      // console.log('Adjusting stock:', {
      //   stockId: stock.id,
      //   currentQuantity: stock.quantity,
      //   adjustment: adjustmentValue,
      //   newQuantity: newQuantity,
      //   reason: reason
      // });

      // Call API to adjust stock
      await stocksAPI.adjustQuantity(stock.id, adjustmentValue, reason);

      // Update local IndexedDB
      await dbHelpers.updateStock(stock.id, { quantity: newQuantity });

      // Sync fresh data
      await syncService.fullSync();

      alert(`✅ Stock adjusted successfully! New quantity: ${newQuantity}`);
      navigate('/inventory/stocks');

    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="stock-adjustment-container">
        <div className="error-state">
          <p>Product not found</p>
          <button onClick={() => navigate('/inventory/stocks')} className="btn-primary">
            Back to Stocks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-adjustment-container fade-in">
      <div className="page-header">
        <h1>Adjust Stock: {product.name}</h1>
        <p className="subtitle">SKU: {product.sku}</p>
      </div>

      {variantDetails && (
  <div style={{
    background: '#1f2937',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    color: '#fff',
    border: '1px solid #10b981'
  }}>
    <strong>Adjusting Variant:</strong>{' '}
    <span style={{color: '#10b981', fontWeight: '600'}}>
      {variantDetails.color} {variantDetails.size}
      {variantDetails.material && ` (${variantDetails.material})`}
    </span>
  </div>
)}

      {error && (
        <div className="alert alert-error">
          ✕ {error}
        </div>
      )}

      <div className="form-card">
        <div className="form-group">
          <label>Warehouse *</label>
          <select
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            disabled={submitting}
            required
          >
            <option value="">Select Warehouse</option>
            {stocks.map(stock => (
              <option key={stock.id} value={stock.warehouse_id || stock.warehouse}>
                {stock.warehouse_name} (Current: {stock.quantity} units)
              </option>
            ))}
          </select>
          {stocks.length === 0 && (
            <small className="help-text error">
              No stock records found for this product
            </small>
          )}
        </div>

        <div className="form-group">
          <label>Adjustment (+/-) *</label>
          <input
            type="number"
            value={adjustment}
            onChange={(e) => setAdjustment(e.target.value)}
            placeholder="e.g., +10 or -5"
            disabled={submitting}
            required
          />
          <small className="help-text">
            Use positive numbers to add stock, negative to reduce
          </small>
        </div>

        <div className="form-group">
          <label>Reason *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for adjustment (e.g., damaged goods, inventory count correction)..."
            rows="3"
            disabled={submitting}
            required
          />
        </div>

        <div className="button-group">
          <button
            onClick={() => navigate('/inventory/stocks')}
            className="btn-secondary"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleAdjust}
            className="btn-primary"
            disabled={submitting || stocks.length === 0}
          >
            {submitting ? 'Adjusting...' : 'Adjust Stock'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default StockAdjustment;
